import { useState } from 'react';
import { Gift } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { adminApi } from '@/services/adminApi';

type BetaPlanId = 'sme_starter' | 'sme_growth' | 'sme_professional';

interface Props {
  uid: string;
  userEmail: string;
  accountType?: string | null;
  onGranted?: () => void;
}

/**
 * Admin one-click beta comp grant — server-side Stripe + BETA_GROWTH1M.
 * No checkout link for the user; requires STRIPE_LIVE_SECRET_KEY on BE.
 */
export function BetaGrantCard({ uid, userEmail, accountType, onGranted }: Props) {
  const [planId, setPlanId] = useState<BetaPlanId>('sme_growth');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastWarnings, setLastWarnings] = useState<string[]>([]);

  if (accountType === 'agency_owner') {
    return null;
  }

  const grant = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setBusy(true);
    setLastWarnings([]);
    try {
      const res = await adminApi.users.betaGrant(uid, {
        planId,
        promotionCode: 'BETA_GROWTH1M',
        reason: reason.trim(),
      });
      setLastWarnings(res.warnings ?? []);
      if (res.autumnSynced) {
        toast.success(`Granted ${res.planId} — user should see Growth limits after refresh`);
      } else {
        toast.warning('Firestore updated; Autumn sync pending — check warnings');
      }
      onGranted?.();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-border/50 border-violet-500/20">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-2">
          <Gift className="h-5 w-5 text-violet-600 mt-0.5 shrink-0" />
          <div>
            <h2 className="text-sm font-semibold">Beta grant — 1 month Growth free</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Applies <code className="text-[11px]">BETA_GROWTH1M</code> server-side (€0 first month).
              No Stripe checkout for {userEmail || 'this user'}. Card not required; month 2 bills at full
              price unless they cancel.
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="beta-plan">Plan</Label>
            <Select value={planId} onValueChange={(v) => setPlanId(v as BetaPlanId)}>
              <SelectTrigger id="beta-plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sme_growth">SME Growth (default)</SelectItem>
                <SelectItem value="sme_starter">SME Starter</SelectItem>
                <SelectItem value="sme_professional">SME Professional</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="beta-reason">Reason (required)</Label>
          <Textarea
            id="beta-reason"
            placeholder="e.g. beta_cohort_june_2026 — Silvia Adamo"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
          />
        </div>

        {lastWarnings.length > 0 && (
          <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-1 list-disc pl-4">
            {lastWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}

        <Button type="button" disabled={busy} onClick={grant} className="gap-1.5">
          {busy ? 'Granting…' : 'Grant beta plan now'}
        </Button>
      </CardContent>
    </Card>
  );
}
